import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";

import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { versionSystem } from "../../../package.json";
import { i18n } from "../../translate/i18n";
import { nomeEmpresa } from "../../../package.json";
import { AuthContext } from "../../context/Auth/AuthContext";
import logo from "../../assets/logo.png";
import BrandingContext from "../../context/Branding/BrandingContext";
import {LanguageOutlined} from "@material-ui/icons";
import {IconButton, Menu, MenuItem} from "@material-ui/core";
import LanguageControl from "../../components/LanguageControl";


const useStyles = makeStyles(theme => ({
	root: {
		width: "100vw",
		height: "100vh",
		//background: "linear-gradient(to right, #682EE3 , #682EE3 , #682EE3)",
		//backgroundImage: "url(https://i.imgur.com/CGby9tN.png)",
		backgroundColor: theme.palette.primary.main,
		backgroundRepeat: "no-repeat",
		backgroundSize: "100% 100%",
		backgroundPosition: "center",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		textAlign: "center",
		position: "relative"
	},
	paper: {
		backgroundColor: theme.palette.login,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: "55px 30px",
		borderRadius: "12.5px",
	},
	avatar: {
		margin: theme.spacing(1),
		backgroundColor: theme.palette.secondary.main,
	},
	form: {
		width: "100%", // Fix IE 11 issue.
		marginTop: theme.spacing(1),
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
	},
	powered: {
		color: "white"
	},
	languageControl: {
		position: "absolute",
		top: 0,
		left: 0,
		paddingLeft: 15
	}
}));

const Login = () => {
	const classes = useStyles();
	const branding = React.useContext(BrandingContext);
	const allowCompanyCreation = process.env.REACT_APP_ENABLE_COMPANY_CREATION === "true";

	const [user, setUser] = useState({ email: "", password: "" });

	// Languages
	const [anchorElLanguage, setAnchorElLanguage] = useState(null);
	const [menuLanguageOpen, setMenuLanguageOpen] = useState(false);

	const { handleLogin, loading } = useContext(AuthContext);
	const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
	const turnstileRequired = Boolean(turnstileSiteKey);
	const turnstileContainerRef = useRef(null);
	const turnstileWidgetIdRef = useRef(null);

	const [turnstileToken, setTurnstileToken] = useState("");
	const [turnstileReady, setTurnstileReady] = useState(!turnstileRequired);

	const handleChangeInput = e => {
		setUser({ ...user, [e.target.name]: e.target.value });
	};

	useEffect(() => {
		if (!turnstileRequired) {
			return;
		}

		const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

		const resetWidgetState = () => {
			setTurnstileToken("");
			setTurnstileReady(false);
		};

		resetWidgetState();

		const renderWidget = () => {
			if (!window.turnstile || !turnstileContainerRef.current) {
				return;
			}

			const widgetId = window.turnstile.render(turnstileContainerRef.current, {
				sitekey: turnstileSiteKey,
				callback: token => {
					setTurnstileToken(token);
					setTurnstileReady(true);
				},
				"error-callback": () => {
					resetWidgetState();
				},
				"expired-callback": () => {
					resetWidgetState();
					if (window.turnstile && turnstileWidgetIdRef.current !== null) {
						window.turnstile.reset(turnstileWidgetIdRef.current);
					}
				}
			});

			turnstileWidgetIdRef.current = widgetId;
		};

		const ensureScript = () => {
			const existingScript = document.querySelector(`script[src="${SCRIPT_SRC}"]`);

			if (existingScript) {
				if (existingScript.getAttribute("data-loaded")) {
					renderWidget();
				} else {
					existingScript.addEventListener("load", renderWidget);
				}
				return existingScript;
			}

			const script = document.createElement("script");
			script.src = SCRIPT_SRC;
			script.async = true;
			script.defer = true;
			script.setAttribute("data-cfasync", "false");
			script.addEventListener("load", () => {
				script.setAttribute("data-loaded", "true");
				renderWidget();
			});
			document.body.appendChild(script);
			return script;
		};

		if (window.turnstile && window.turnstile.render) {
			renderWidget();
		} else {
			ensureScript();
		}

		return () => {
			const script = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
			if (script) {
				script.removeEventListener("load", renderWidget);
			}

			if (window.turnstile && turnstileWidgetIdRef.current !== null) {
				window.turnstile.remove(turnstileWidgetIdRef.current);
				turnstileWidgetIdRef.current = null;
			}
		};
	}, [turnstileRequired, turnstileSiteKey]);

	const resetTurnstile = () => {
		if (turnstileRequired && window.turnstile && turnstileWidgetIdRef.current !== null) {
			window.turnstile.reset(turnstileWidgetIdRef.current);
		}
		setTurnstileToken("");
		setTurnstileReady(!turnstileRequired);
	};

	const handlSubmit = async e => {
		e.preventDefault();

		if (turnstileRequired && !turnstileToken) {
			return;
		}

		try {
			await handleLogin({ ...user, turnstileToken });
		} catch (err) {
			resetTurnstile();
		}
	};

	const handlemenuLanguage = ( event ) => {
		setAnchorElLanguage(event.currentTarget);
		setMenuLanguageOpen( true );
	}

	const handleCloseMenuLanguage = (  ) => {
		setAnchorElLanguage(null);
		setMenuLanguageOpen(false);
	}

	return (
		<div className={classes.root}>
		<div className={classes.languageControl}>
			<IconButton edge="start">
				<LanguageOutlined
					aria-label="account of current user"
					aria-controls="menu-appbar"
					aria-haspopup="true"
					onClick={handlemenuLanguage}
					variant="contained"
					style={{ color: "white",marginRight:10 }}
				/>
			</IconButton>
			<Menu
				id="menu-appbar-language"
				anchorEl={anchorElLanguage}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuLanguageOpen}
				onClose={handleCloseMenuLanguage}
			>
				<MenuItem>
					<LanguageControl />
				</MenuItem>
			</Menu>
		</div>
		<Container component="main" maxWidth="xs">
			<CssBaseline/>
			<div className={classes.paper}>
				<div>
					<img style={{ margin: "0 auto", width: "70%" }} src={branding?.logoUrl || logo} alt={branding?.siteName || "Logo"} />
				</div>
				{/*<Typography component="h1" variant="h5">
					{i18n.t("login.title")}
				</Typography>*/}
				<form className={classes.form} noValidate onSubmit={handlSubmit}>
					<TextField
						variant="outlined"
						margin="normal"
						required
						fullWidth
						id="email"
						label={i18n.t("login.form.email")}
						name="email"
						value={user.email}
						onChange={handleChangeInput}
						autoComplete="email"
						autoFocus
					/>
					<TextField
						variant="outlined"
						margin="normal"
						required
						fullWidth
						name="password"
						label={i18n.t("login.form.password")}
						type="password"
						id="password"
						value={user.password}
						onChange={handleChangeInput}
						autoComplete="current-password"
					/>
					{/* <Grid container justify="flex-end">
					  <Grid item xs={6} style={{ textAlign: "right" }}>
						<Link component={RouterLink} to="/forgetpsw" variant="body2">
						  Esqueceu sua senha?
						</Link>
					  </Grid>
					</Grid>*/}

					{turnstileRequired && (
						<Box mt={2} display="flex" justifyContent="center">
							<div ref={turnstileContainerRef} />
						</Box>
					)}
					<Button
						type="submit"
						fullWidth
						variant="contained"
						color="primary"
						className={classes.submit}
						disabled={loading || (turnstileRequired && (!turnstileToken || !turnstileReady))}
					>
						{i18n.t("login.buttons.submit")}
					</Button>
						{allowCompanyCreation && (
							<Grid container>
								<Grid item>
									<Link
										variant="body2"
										component={RouterLink}
										to="/signup"
									>
										{i18n.t("login.buttons.register")}
									</Link>
								</Grid>
							</Grid>
						)}
				</form>

			</div>
		</Container>
		</div>
	);
};

export default Login;
